/**
 * Authorized Route Component
 * Route guard that checks user permissions and shows ForbiddenPage when unauthorized
 */

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ForbiddenPage } from '../../pages/ForbiddenPage';
import { hasPermission, hasAnyPermission, type PermissionValue } from '../../types/auth';

/**
 * AuthorizedRoute Props
 */
interface AuthorizedRouteProps {
  /**
   * Child components to render when authorized
   */
  children: React.ReactNode;

  /**
   * Single permission required to access this route
   */
  requiredPermission?: PermissionValue;

  /**
   * Any of these permissions allows access (OR logic)
   */
  requiredPermissions?: PermissionValue[];
}

/**
 * AuthorizedRoute Component
 * Wraps protected routes and checks user permissions before rendering.
 * Shows ForbiddenPage (403) when user lacks required permissions.
 *
 * @example
 * ```tsx
 * // Single permission check
 * <AuthorizedRoute requiredPermission={Permission.MANAGE_ALL_ORGANIZATIONS}>
 *   <OrganizationsPage />
 * </AuthorizedRoute>
 *
 * // Any of multiple permissions
 * <AuthorizedRoute requiredPermissions={[Permission.MANAGE_TENANT_USERS, Permission.MANAGE_ALL_ORGANIZATIONS]}>
 *   <UserManagementPage />
 * </AuthorizedRoute>
 * ```
 */
export const AuthorizedRoute: React.FC<AuthorizedRouteProps> = ({
  children,
  requiredPermission,
  requiredPermissions,
}) => {
  const { user } = useAuth();

  // Check single required permission
  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    return <ForbiddenPage />;
  }

  // Check multiple required permissions (any match is sufficient)
  if (
    requiredPermissions &&
    requiredPermissions.length > 0 &&
    !hasAnyPermission(user, requiredPermissions)
  ) {
    return <ForbiddenPage />;
  }

  // User is authorized, render children
  return <>{children}</>;
};
